// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import {
  Anchor,
  Box,
  Button,
  Group,
  Loader,
  Modal,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { formatHumanName } from '@medplum/core';
import type { Communication, Organization, Patient, Practitioner, Resource } from '@medplum/fhirtypes';
import {
  IconAt,
  IconDeviceMobileMessage,
  IconDeviceTablet,
  IconMail,
  IconPhone,
  IconPlus,
  IconSearch,
  IconUsers,
  IconVideo,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { type JSX, useMemo, useState } from 'react';
import { TableAnchor } from '../../../components/layout/TableAnchor';
import { TableBadge } from '../../../components/TableBadge';
import { usePatient } from '../../../hooks/usePatient';
import { type SenderMap, usePatientCommunications } from '../../../hooks/usePatientCommunications';
import { NewCommunicationModal } from './NewCommunicationModal';

dayjs.extend(relativeTime);

const PAGE_SIZE = 10;

function formatSent(value: string | undefined): string {
  if (!value) {
    return '—';
  }
  return dayjs(value).fromNow();
}

function formatSentAbsolute(value: string | undefined): string {
  if (!value) {
    return '—';
  }
  return dayjs(value).format('MMM D, YYYY [at] h:mma');
}

// VintaSend's Medplum backend writes automated notifications as Communication
// resources with meta.tag carrying the notification marker, a human context
// name (e.g. taskAssignment, inboxMessage, patientWelcome), and a delivery
// type (EMAIL / SMS / IN_APP). See vintasend-medplum/dist/medplum-backend.js.
const VINTASEND_MARKER_TAG = 'notification';
const MANUAL_ENTRY_TAG = 'manual-entry';
const VINTASEND_DELIVERY_TYPES = new Set(['EMAIL', 'SMS', 'IN_APP']);
const VINTASEND_CONTEXT_LABELS: Record<string, string> = {
  taskAssignment: 'Task Assignment',
  taskDueSoon: 'Task Due Soon',
  inboxMessage: 'Message',
  patientWelcome: 'Welcome',
};

function getTagCodes(communication: Communication): string[] {
  return (communication.meta?.tag ?? []).map((t) => t.code ?? '').filter(Boolean);
}

function getSource(communication: Communication): 'automated' | 'manual' {
  const tags = getTagCodes(communication);
  return tags.includes(VINTASEND_MARKER_TAG) && !tags.includes(MANUAL_ENTRY_TAG) ? 'automated' : 'manual';
}

function getDeliveryStatus(communication: Communication): 'pending' | 'sent' {
  return communication.status === 'completed' ? 'sent' : 'pending';
}

function getType(communication: Communication): string {
  const tags = getTagCodes(communication);
  if (tags.includes(VINTASEND_MARKER_TAG)) {
    const contextTag = tags.find((code) => code !== VINTASEND_MARKER_TAG && !VINTASEND_DELIVERY_TYPES.has(code));
    if (contextTag) {
      return VINTASEND_CONTEXT_LABELS[contextTag] ?? contextTag;
    }
  }
  return communication.category?.[0]?.coding?.[0]?.display ?? 'Message';
}

function getMedium(communication: Communication): { label: string; icon: JSX.Element } {
  const vintasendType = getTagCodes(communication).find((code) => VINTASEND_DELIVERY_TYPES.has(code));
  const raw = vintasendType ?? communication.medium?.[0]?.coding?.[0]?.code ?? '';
  const normalized = raw.toLowerCase();
  if (normalized === 'email' || normalized === 'onlinwrit' || normalized === 'electronic') {
    return { label: 'Email', icon: <IconAt size={12} /> };
  }
  if (normalized === 'sms') {
    return { label: 'SMS', icon: <IconDeviceMobileMessage size={12} /> };
  }
  if (normalized === 'phone') {
    return { label: 'Phone Call', icon: <IconPhone size={12} /> };
  }
  if (normalized === 'in-person' || normalized === 'face') {
    return { label: 'In Person', icon: <IconUsers size={12} /> };
  }
  if (normalized === 'video' || normalized === 'videoconf') {
    return { label: 'Video Call', icon: <IconVideo size={12} /> };
  }
  if (normalized === 'mail' || normalized === 'written') {
    return { label: 'Mail', icon: <IconMail size={12} /> };
  }
  if (normalized === 'in_app' || normalized.includes('in-app') || normalized.includes('app')) {
    return { label: 'In-App', icon: <IconDeviceTablet size={12} /> };
  }
  return { label: raw || 'Secure Message', icon: <IconDeviceTablet size={12} /> };
}

function getPreview(communication: Communication): string {
  const first = communication.payload?.find((p) => typeof p.contentString === 'string')?.contentString;
  return first ?? '—';
}

function getSenderLabel(communication: Communication, senders: SenderMap): string {
  if (communication.sender?.display) {
    return communication.sender.display;
  }
  const ref = communication.sender?.reference;
  if (!ref) {
    return '—';
  }
  const resource = senders.get(ref);
  if (!resource) {
    return ref;
  }
  if (resource.resourceType === 'Practitioner') {
    const name = (resource as Practitioner).name?.[0];
    return name ? formatHumanName(name) : 'Practitioner';
  }
  if (resource.resourceType === 'Organization') {
    return (resource as Organization).name ?? 'Organization';
  }
  if (resource.resourceType === 'Patient') {
    return 'Patient';
  }
  return ref;
}

interface CommunicationDetailModalProps {
  communication: Communication | undefined;
  sender: Resource | undefined;
  opened: boolean;
  onClose: () => void;
}

function formatDetailSender(sender: Resource | undefined, communication: Communication): string {
  if (sender?.resourceType === 'Practitioner') {
    return formatHumanName((sender as Practitioner).name?.[0] ?? {});
  }
  if (sender?.resourceType === 'Organization') {
    return (sender as Organization).name ?? 'Organization';
  }
  return communication.sender?.display ?? '—';
}

function CommunicationDetailModal({
  communication,
  sender,
  opened,
  onClose,
}: CommunicationDetailModalProps): JSX.Element {
  const title = communication ? getType(communication) : 'Communication';
  return (
    <Modal opened={opened} onClose={onClose} title={title} centered size="lg">
      {communication ? (
        <Stack gap="sm">
          <Group gap="lg" wrap="wrap">
            <Text size="sm">
              <Text span fw={600}>
                Sent:{' '}
              </Text>
              {formatSentAbsolute(communication.sent)}
            </Text>
            <Text size="sm">
              <Text span fw={600}>
                From:{' '}
              </Text>
              {formatDetailSender(sender, communication)}
            </Text>
            <Text size="sm">
              <Text span fw={600}>
                Medium:{' '}
              </Text>
              {getMedium(communication).label}
            </Text>
            <Text size="sm">
              <Text span fw={600}>
                Status:{' '}
              </Text>
              {communication.status ?? '—'}
            </Text>
          </Group>
          <Stack gap="xs" mt="sm">
            {(communication.payload ?? []).map((payload, idx) => {
              if (typeof payload.contentString === 'string') {
                return (
                  <Text key={`str-${idx}-${payload.contentString.slice(0, 16)}`} style={{ whiteSpace: 'pre-wrap' }}>
                    {payload.contentString}
                  </Text>
                );
              }
              if (payload.contentAttachment?.url) {
                return (
                  <Anchor
                    key={`att-${idx}-${payload.contentAttachment.url}`}
                    href={payload.contentAttachment.url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {payload.contentAttachment.title ?? 'Attachment'}
                  </Anchor>
                );
              }
              return null;
            })}
            {!communication.payload?.length && <Text c="dimmed">No payload on this message.</Text>}
          </Stack>
        </Stack>
      ) : null}
    </Modal>
  );
}

export function CommunicationLogTab(): JSX.Element {
  const patient = usePatient();
  const { communications, senders, loading, refresh } = usePatientCommunications(patient);
  const [query, setQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Communication | undefined>();
  const [newEntryOpen, setNewEntryOpen] = useState(false);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return communications.filter((c) => {
      const sourceMatches = !sourceFilter || getSource(c) === sourceFilter;
      const statusMatches = !statusFilter || getDeliveryStatus(c) === statusFilter;
      const hay = [getType(c), getSenderLabel(c, senders), getMedium(c).label, getPreview(c)].join(' ').toLowerCase();
      return sourceMatches && statusMatches && (!q || hay.includes(q));
    });
  }, [communications, query, senders, sourceFilter, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageSafe = Math.min(page, totalPages);
  const paginated = filtered.slice((pageSafe - 1) * PAGE_SIZE, pageSafe * PAGE_SIZE);

  if (!patient) {
    return (
      <Stack align="center" p="md">
        <Loader size="sm" />
      </Stack>
    );
  }

  const selectedSender = selected?.sender?.reference ? senders.get(selected.sender.reference) : undefined;

  return (
    <Stack gap="lg" p="md">
      <Paper withBorder radius="md" shadow="sm" p="xl">
        <Group justify="space-between" mb="md" wrap="wrap">
          <Title order={2}>Communications Log</Title>
          <Group gap="sm">
            <TextInput
              placeholder="Search…"
              value={query}
              onChange={(e) => {
                setQuery(e.currentTarget.value);
                setPage(1);
              }}
              leftSection={<IconSearch size={14} />}
              size="sm"
              w={260}
              aria-label="Search communications"
            />
            <Select
              placeholder="Source"
              data={[
                { value: 'automated', label: 'Automated' },
                { value: 'manual', label: 'Manual' },
              ]}
              value={sourceFilter}
              onChange={(value) => {
                setSourceFilter(value);
                setPage(1);
              }}
              clearable
              size="sm"
              w={140}
              aria-label="Filter communications by source"
            />
            <Select
              placeholder="Status"
              data={[
                { value: 'pending', label: 'Pending' },
                { value: 'sent', label: 'Sent' },
              ]}
              value={statusFilter}
              onChange={(value) => {
                setStatusFilter(value);
                setPage(1);
              }}
              clearable
              size="sm"
              w={130}
              aria-label="Filter communications by status"
            />
            <Button leftSection={<IconPlus size={16} />} size="sm" onClick={() => setNewEntryOpen(true)}>
              New Entry
            </Button>
          </Group>
        </Group>

        {loading ? (
          <Stack align="center" py="md">
            <Loader size="sm" />
          </Stack>
        ) : (
          <Box style={{ overflowX: 'auto' }}>
            <Table highlightOnHover withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Sent</Table.Th>
                  <Table.Th>Type</Table.Th>
                  <Table.Th>From</Table.Th>
                  <Table.Th>Medium</Table.Th>
                  <Table.Th>Preview</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {paginated.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={7}>
                      <Text ta="center" c="dimmed" py="md">
                        No communications on file.
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  paginated.map((communication) => {
                    const medium = getMedium(communication);
                    const type = getType(communication);
                    return (
                      <Table.Tr key={communication.id}>
                        <Table.Td>
                          <Tooltip label={formatSentAbsolute(communication.sent)}>
                            <span>{formatSent(communication.sent)}</span>
                          </Tooltip>
                        </Table.Td>
                        <Table.Td>
                          <TableBadge variant="light" color={type === 'Message' ? 'gray' : 'polaris'}>
                            {type}
                          </TableBadge>
                        </Table.Td>
                        <Table.Td>{getSenderLabel(communication, senders)}</Table.Td>
                        <Table.Td>
                          <TableBadge variant="default" leftSection={medium.icon}>
                            {medium.label}
                          </TableBadge>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" c="dimmed" lineClamp={1} maw={260}>
                            {getPreview(communication)}
                          </Text>
                        </Table.Td>
                        <Table.Td>{getDeliveryStatus(communication) === 'sent' ? 'Sent' : 'Pending'}</Table.Td>
                        <Table.Td>
                          <TableAnchor
                            component="button"
                            type="button"
                            size="sm"
                            onClick={() => setSelected(communication)}
                          >
                            View
                          </TableAnchor>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })
                )}
              </Table.Tbody>
            </Table>
          </Box>
        )}

        {filtered.length > PAGE_SIZE && (
          <Group justify="space-between" mt="sm">
            <Text size="sm" c="dimmed">{`${filtered.length} of ${communications.length}`}</Text>
            <Pagination value={pageSafe} onChange={setPage} total={totalPages} size="sm" />
          </Group>
        )}
      </Paper>

      <CommunicationDetailModal
        communication={selected}
        sender={selectedSender}
        opened={Boolean(selected)}
        onClose={() => setSelected(undefined)}
      />

      <NewCommunicationModal
        patient={patient}
        opened={newEntryOpen}
        onClose={() => setNewEntryOpen(false)}
        onSaved={() => {
          refresh();
        }}
      />
    </Stack>
  );
}
